/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  FormControlLabel,
  IconButton,
  OutlinedInput,
  Typography,
} from '@material-ui/core';
import { BiSortUp, BiSortDown } from 'react-icons/bi';
import { FaSearch } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import { IEquipmentRelevance } from '../../../types/kpis';

interface IFilterModalSimplified extends DialogProps {
  equipments: IEquipmentRelevance[];
  onDone?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  filtered?: (equipments: IEquipmentRelevance[]) => void;
}

const FilterModalSimplified = React.forwardRef<any, IFilterModalSimplified>(
  ({ equipments, onDone, onClose, filtered, ...rest }, ref) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const [sort, setSort] = useState('');
    const [filteredEquipments, setFilteredEquipments] = useState<
      IEquipmentRelevance[]
    >([]);

    const handleClose = (): void => {
      setSearch('');
      setActiveFilters([]);
      setSort('');
      setFilteredEquipments(equipments);
      if (onClose) {
        onClose();
      }
    };

    const handleChangeFilter = (
      equipmentName: string,
      checked: boolean,
    ): void => {
      if (equipmentName === 'all') {
        if (checked) {
          const allEquipments = equipments.map(eq => eq.name);
          setActiveFilters(allEquipments);
        } else {
          setActiveFilters([]);
        }
      } else {
        if (checked) {
          setActiveFilters([...activeFilters, equipmentName]);
          return;
        }
        const newFilters = [...activeFilters];
        const eqIndex = newFilters.findIndex(name => name === equipmentName);

        if (eqIndex !== -1) {
          newFilters.splice(eqIndex, 1);
        }

        setActiveFilters(newFilters);
      }
    };

    const handleSort = useCallback(
      (type: string): IEquipmentRelevance[] => {
        if (type === 'asc') {
          const sorted = equipments.sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });

          return sorted;
        }
        const sorted = equipments.sort((a, b) => {
          if (b.name < a.name) {
            return -1;
          }
          if (b.name > a.name) {
            return 1;
          }
          return 0;
        });

        return sorted;
      },
      [equipments],
    );

    const handleDone = (): void => {
      if (activeFilters.length > 0) {
        const filteredData: IEquipmentRelevance[] = [];
        for (let i = 0; i < activeFilters.length; i += 1) {
          const equip = equipments.find(
            eq => eq.name.toLowerCase() === activeFilters[i].toLowerCase(),
          );
          if (equip) {
            filteredData.push(equip);
          }
        }

        if (filteredData.length > 0) {
          setFilteredEquipments(filteredData);
          if (filtered) {
            filtered(filteredData);
          }
        }
        if (onDone) {
          onDone();
        }
      }
    };

    useEffect(() => {
      if (search !== '') {
        const filteredEquip = equipments.filter(eq =>
          eq.name.toLowerCase().includes(search.toLowerCase()),
        );
        setFilteredEquipments(filteredEquip);
        return;
      }
      setFilteredEquipments([]);
    }, [search, equipments]);

    useEffect(() => {
      if (sort !== '') {
        const res = handleSort(sort);
        setFilteredEquipments(res);
        return;
      }
      setFilteredEquipments([]);
    }, [sort, handleSort]);

    // Expose clearFilter to other components
    useImperativeHandle(ref, () => ({
      clearFilter: () => {
        setFilteredEquipments([]);
        setActiveFilters([]);
      },
    }));

    return (
      <Dialog
        className={classes.container}
        aria-labelledby={t('screens:dimensioning.editWeekDialogAria')}
        aria-describedby={t(
          'screens:dimensioning.editWeekDialogAriaDescribedBy',
        )}
        {...rest}
      >
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={() => handleClose()}
        >
          <FiX color="hsla(0, 0%, 20%, 1)" size={16} />
        </IconButton>
        <DialogContent className={classes.content}>
          <div className={classes.inputContainer}>
            <form className={classes.form} noValidate>
              <Typography variant="subtitle1" className={classes.formLabel}>
                {t('screens:dimensioning.classify')}
              </Typography>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-around',
                }}
              >
                <Chip
                  label={t('components:utils.ascendingOrder')}
                  icon={<BiSortUp />}
                  onClick={() => setSort('asc')}
                  className={clsx(classes.sortButton, {
                    [classes.sortButtonActive]: sort === 'asc',
                  })}
                />
                <Chip
                  label={t('components:utils.descendingOrder')}
                  icon={<BiSortDown />}
                  onClick={() => setSort('desc')}
                  className={clsx(classes.sortButton, {
                    [classes.sortButtonActive]: sort === 'desc',
                  })}
                />
                <OutlinedInput
                  id="outlined-search"
                  type="search"
                  placeholder={t('components:forms.search')}
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  className={classes.search}
                  startAdornment={
                    <FaSearch
                      color="hsla(216, 11%, 65%, 1)"
                      style={{ marginRight: 8 }}
                    />
                  }
                />
              </div>

              <Typography variant="subtitle1" className={classes.formLabel}>
                {t('components:buttons.filter')}
              </Typography>
              <div className={classes.selectEquipment}>
                <FormControlLabel
                  aria-label="all_equipments"
                  onClick={event => event.stopPropagation()}
                  onFocus={event => event.stopPropagation()}
                  placeholder="Select"
                  control={
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={activeFilters.length === equipments.length}
                      onChange={event =>
                        handleChangeFilter('all', event.target.checked)
                      }
                    />
                  }
                  label={t('components:forms.selectAll')}
                  className={classes.checkbox}
                />
                {filteredEquipments.length > 0
                  ? filteredEquipments.map(eq => (
                      <FormControlLabel
                        key={eq.id}
                        aria-label={`equipment_${eq.name}`}
                        onClick={event => event.stopPropagation()}
                        onFocus={event => event.stopPropagation()}
                        placeholder="Select"
                        control={
                          <Checkbox
                            color="primary"
                            size="small"
                            checked={activeFilters.includes(eq.name)}
                            onChange={event =>
                              handleChangeFilter(eq.name, event.target.checked)
                            }
                          />
                        }
                        label={eq.name}
                        className={classes.checkbox}
                      />
                    ))
                  : equipments.map(eq => (
                      <FormControlLabel
                        key={eq.id}
                        aria-label={`equipment_${eq.name}`}
                        onClick={event => event.stopPropagation()}
                        onFocus={event => event.stopPropagation()}
                        placeholder="Select"
                        control={
                          <Checkbox
                            color="primary"
                            size="small"
                            checked={activeFilters.includes(eq.name)}
                            onChange={event =>
                              handleChangeFilter(eq.name, event.target.checked)
                            }
                          />
                        }
                        label={eq.name}
                        className={classes.checkbox}
                      />
                    ))}
              </div>
            </form>
          </div>
        </DialogContent>
        <DialogActions className={classes.buttonsContainer}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => handleClose()}
            color="primary"
            className={classes.button}
            disabled={activeFilters.length === 0}
          >
            {t('components:buttons.clear')}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleDone()}
            color="primary"
            className={classes.button}
            disabled={activeFilters.length === 0}
          >
            {t('components:buttons.apply')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default FilterModalSimplified;
